import React, { useCallback, useRef } from "react";
import styled from "@emotion/styled";
import { FloatingNotePreview, HEADS_UP_PREVIEW_PLACEMENT } from "@/components/item-preview";
import {
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
  MDSChip,
  MDSIconKind,
  MDSChipVariant,
} from "@/mds";
import { formatRelativeDayLabel } from "./date-utils";
import type { FC } from "react";
import { observer } from "mobx-react-lite";
import type { Maybe } from "@/domains/common/types";
import type { SmartNotePreviewInfo } from "./smart-note-preview.types";
import { useHeadsUpPreviewHover } from "./use-heads-up-preview-hover";

export interface SmartTimelineRowProps {
  noteId?: string;
  primary: string;
  datetime: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  notePreviewInfo?: Maybe<SmartNotePreviewInfo>;
  noteTitle?: string;
  showTopConnector?: boolean;
  showBottomConnector?: boolean;
  onPreviewOpen?: () => void;
}

export const SmartTimelineRow: FC<SmartTimelineRowProps> = observer(
  ({
    noteId: _noteId,
    primary,
    datetime,
    onClick,
    notePreviewInfo,
    noteTitle,
    showTopConnector = false,
    showBottomConnector = false,
    onPreviewOpen,
  }) => {
    const rowRef = useRef<HTMLDivElement | null>(null);
    const { hoverState, handleMouseEnter, handleMouseLeave } = useHeadsUpPreviewHover({
      noteId: _noteId,
      notePreviewInfo,
      onPreviewOpen,
      resolveAnchor: () => {
        const rowEl = rowRef.current;
        if (!rowEl) return null;
        const chipEl = rowEl.querySelector('[data-mds-name="chip"]') as HTMLElement | null;
        return chipEl ?? rowEl;
      },
    });

    const handleRowClick = useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        onClick?.(e);
      },
      [onClick]
    );

    return (
      <SmartTimelineRowWrapper>
        <TimelineRow
          ref={rowRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleRowClick}
        >
          <ConnectorColumn>
            <ConnectorTop $visible={showTopConnector}>
              <line
                x1="1"
                y1="0"
                x2="1"
                y2="100%"
                stroke="currentColor"
                strokeWidth={1}
                strokeDasharray="2 2"
              />
            </ConnectorTop>
            <ConnectorDot />
            <ConnectorBottom $visible={showBottomConnector}>
              <line
                x1="1"
                y1="0"
                x2="1"
                y2="100%"
                stroke="currentColor"
                strokeWidth={1}
                strokeDasharray="2 2"
              />
            </ConnectorBottom>
          </ConnectorColumn>
          <TimelineContent>
            <TimelineDateText variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
              {formatRelativeDayLabel(datetime)}
            </TimelineDateText>
            <TitleText
              title={primary}
              variant={MDSTextVariant.TextS}
              weight={MDSTextWeight.Regular}
            >
              {primary}
            </TitleText>
            {noteTitle ? (
              <SmallNoteChip
                icon={MDSIconKind.Document}
                variant={MDSChipVariant.Small}
                aria-label={noteTitle}
              >
                {noteTitle}
              </SmallNoteChip>
            ) : null}
          </TimelineContent>
        </TimelineRow>

        {hoverState.hovering && notePreviewInfo?.noteObservable && (
          <FloatingNotePreview
            state={hoverState}
            noteObservable={notePreviewInfo.noteObservable}
            placement={HEADS_UP_PREVIEW_PLACEMENT}
          />
        )}
      </SmartTimelineRowWrapper>
    );
  }
);

const SmartTimelineRowWrapper = styled.div`
  position: relative;
`;

const TimelineRow = styled.div`
  position: relative;
  display: flex;
  gap: ${({ theme }) => theme.spacing["1"]};
  min-height: 50px;
  padding: 0px 10.5px;

  border-radius: 8px;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.background};

  background-color: ${({ theme }) => theme.colors.background.button.transparent.default};

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.button.transparent.hover};
    --chip-bg: ${({ theme }) => theme.colors.background.button.chip.hover};
  }

  &:active {
    background-color: ${({ theme }) => theme.colors.background.button.transparent.hover};
  }
`;

export const ConnectorColumn = styled.div`
  position: relative;
  width: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const ConnectorSvg = styled.svg<{ $visible: boolean }>`
  position: absolute;
  left: 50%;
  transform: translateX(calc(-50% + 1.5px));
  width: 5px;
  height: 8px;
  color: ${({ theme }) => theme.colors.border.onAccent};
  visibility: ${({ $visible }) => ($visible ? "visible" : "hidden")};
`;

export const ConnectorTop = styled(ConnectorSvg)`
  top: 0px;
`;

export const ConnectorBottom = styled(ConnectorSvg)`
  top: 16px;
  height: calc(100% - 16px);
`;

export const ConnectorDot = styled.div`
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translate(-50%, 0);
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background-color: ${({ theme }) => theme.colors.icon.emphasis};
  z-index: 1;
`;

export const TimelineContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
  padding-top: 2px;
  padding-bottom: 10px;
  margin-left: 2px;
`;

export const TimelineDateText = styled(MDSText)`
  color: ${({ theme }) => theme.colors.text.emphasis};
  margin-top: 2px;
`;

const TitleText = styled(MDSText)`
  white-space: wrap;
`;

const SmallNoteChip = styled(MDSChip)`
  display: inline-flex;
  align-self: flex-start;
  margin-left: -1px;

  user-select: none;
  pointer-events: none;

  transition: ${({ theme }) => theme.transitions.background};

  background-color: ${({ theme }) =>
    `var(--chip-bg, ${theme.colors.background.button.chip.default})`};

  &[data-hovered]:not([data-disabled]) {
    background-color: ${({ theme }) => theme.colors.background.button.chip.hover};
  }

  margin-bottom: 2px;

  .mds-icon {
    font-size: 11px;
  }
`;
